/* eslint-disable max-lines-per-function */
import React from 'react';
import pencilIcon from '../../../../../Assets/images/svg/dar_pencil.svg';

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const PROJECT_MILESTONE_TABLE_FLAGS = {
  ms_v: 0,
  po_number_view: 0,
  po_link_view: 0,
  ms_amt: 0,
  replanned_due_date_view: 0,
  ms_inv: 0,
  ms_rdt: 0,
  ms_rmk: 0,
  collection_amount_view: 0,
  collection_date_view: 0,
  ms_e: 0,
  ms_a: 0,
};

const HIDDEN_COLLECTION_COLUMN_KEYS = new Set([
  'collection_id',
  'amount_to_collect',
  'collection_due_date',
  'collection_projection_date',
  'collected_date',
  'collected_amount',
  'collection_comment',
  'collection_remarks',
]);

const isBlankValue = (value) => value === null || value === undefined || value === '';

const renderCellText = (value) => (isBlankValue(value) ? '' : value);

export const getColumnClassName = (key) => `col-${String(key || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')}`;

const GROUPED_PARENT_ROW_TYPES = new Set(['milestone']);

const getMilestoneGroupKey = (row, index) => String(
  row?.row_id
  || [row?.po_number, row?.milestone_id, row?.row_type, index].filter(Boolean).join('-')
  || `milestone-group-${index}`,
);

export const buildMilestoneGroups = (rows = []) => {
  const groups = [];
  let currentGroup = null;

  rows.forEach((row, index) => {
    const normalizedRowType = String(row?.row_type || '')
      .trim()
      .toLowerCase();
    const isParentRow = GROUPED_PARENT_ROW_TYPES.has(normalizedRowType) || !currentGroup;

    if (isParentRow) {
      currentGroup = {
        key: getMilestoneGroupKey(row, index),
        parent: row,
        children: [],
      };
      groups.push(currentGroup);
      return;
    }

    currentGroup.children.push(row);
  });

  return groups;
};

const formatDateValue = (value) => {
  if (isBlankValue(value)) return '';
  const isoLikeValue = String(value).split('T')[0].split(' ')[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoLikeValue);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const monthIndex = Number(month) - 1;
    const monthLabel = monthNames[monthIndex];
    return monthLabel ? `${Number(day)}-${monthLabel}-${year}` : String(value);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const monthLabel = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${monthLabel}-${year}`;
};

const formatCurrencyValue = (value) => {
  if (isBlankValue(value)) return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return CURRENCY_FORMATTER.format(amount);
};

const formatPercentValue = (value) => {
  if (isBlankValue(value)) return '';
  const percent = Number(value);
  if (Number.isNaN(percent)) return String(value);
  return `${percent}%`;
};

const renderLinkValue = (value) => {
  if (isBlankValue(value)) return '';
  return (
    <a
      className="milestone-link-cell"
      href={value}
      target="_blank"
      rel="noreferrer"
    >
      View Link
    </a>
  );
};

const getStatusClassName = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalized) return '';
  if (normalized.includes('complete') || normalized.includes('receive') || normalized.includes('paid')) {
    return 'is-success';
  }
  if (normalized.includes('progress') || normalized.includes('raised') || normalized.includes('partial')) {
    return 'is-progress';
  }
  if (normalized.includes('hold') || normalized.includes('delay') || normalized.includes('overdue')) {
    return 'is-warning';
  }
  return 'is-default';
};

const renderStatusValue = (value) => {
  if (isBlankValue(value)) return '';
  return (
    <span className={`milestone-status-chip ${getStatusClassName(value)}`.trim()}>
      {value}
    </span>
  );
};

const renderActionButton = (label, row, actionName, onClick, { disabled = false } = {}) => {
  return (
    <button
      type="button"
      className={`action-btn milestone-row-action milestone-row-action-${actionName}`}
      aria-label={`${label} ${row.row_type || 'row'}`}
      onClick={disabled || typeof onClick !== 'function' ? undefined : () => onClick(row)}
      disabled={disabled || typeof onClick !== 'function'}
    >
      <img src={pencilIcon} alt="" aria-hidden="true" />
    </button>
  );
};

const getRowActionHandlers = (row, {
  onMilestoneEdit,
  onInvoiceAdd,
  onInvoiceEdit,
  onCollectionAdd,
  onCollectionEdit,
}) => {
  const normalizedRowType = String(row?.row_type || '')
    .trim()
    .toLowerCase();

  if (normalizedRowType === 'milestone') {
    return {
      edit: onMilestoneEdit || null,
      add: onInvoiceAdd ? () => onInvoiceAdd({ milestoneId: row?.milestone_id }) : null,
    };
  }

  if (normalizedRowType === 'invoice') {
    return {
      edit: onInvoiceEdit || null,
      add: onCollectionAdd || null,
    };
  }

  if (normalizedRowType === 'collection') {
    return {
      edit: onCollectionEdit || null,
      add: null,
    };
  }

  return {
    edit: null,
    add: null,
  };
};

const renderActionButtons = (row, flags, callbacks) => {
  const normalizedRowType = String(row?.row_type || '')
    .trim()
    .toLowerCase();
  const hasMilestoneIdentity = !isBlankValue(row?.milestone_id);

  if (normalizedRowType !== 'milestone' || !hasMilestoneIdentity) return '';

  const handlers = getRowActionHandlers(row, callbacks);
  const canEdit = Boolean(flags.ms_e) && typeof handlers.edit === 'function';

  if (!canEdit) return '';

  return (
    <div className="action-btn-group">
      {canEdit ? renderActionButton('Edit', row, 'edit', handlers.edit) : null}
    </div>
  );
};

export const buildTotalsRow = (totals = {}, columns = []) => {
  const totalLabelKey = 'milestone_id';
  return columns.reduce((acc, column) => {
    if (column.key === totalLabelKey) {
      acc[column.key] = 'Total';
    } else if (column.key === 'cont_value_p') {
      acc[column.key] = totals?.contractValuePercent ?? '';
    } else if (column.key === 'amount') {
      acc[column.key] = totals?.amount ?? '';
    } else if (column.key === 'cogs') {
      acc[column.key] = totals?.cogs ?? '';
    } else if (column.key === 'ga') {
      acc[column.key] = totals?.ga ?? '';
    } else if (column.key === 'tp') {
      acc[column.key] = totals?.tp ?? '';
    } else if (column.key === 'invoice_amount') {
      acc[column.key] = totals?.invoiceAmount ?? '';
    } else if (column.key === 'invoice_cogs') {
      acc[column.key] = totals?.invoiceCogs ?? '';
    } else if (column.key === 'invoice_ga') {
      acc[column.key] = totals?.invoiceGa ?? '';
    } else if (column.key === 'invoice_tp') {
      acc[column.key] = totals?.invoiceTp ?? '';
    } else if (column.key === 'invoice_amount_with_tax') {
      acc[column.key] = totals?.invoiceAmountWithTax ?? '';
    } else if (column.key === 'amount_to_collect') {
      acc[column.key] = totals?.amountToCollect ?? '';
    } else if (column.key === 'collected_amount') {
      acc[column.key] = totals?.collectedAmount ?? '';
    } else {
      acc[column.key] = '';
    }
    return acc;
  }, {
    row_id: 'milestone-totals-row',
    row_type: 'totals',
  });
};

export const renderExpandableIdentityCell = ({
  row,
  renderedValue,
  firstIdentityColumnKey,
  columnKey,
  onToggle,
}) => {
  if (columnKey !== firstIdentityColumnKey) return renderedValue;

  if (row._isGroupParent && row._hasChildren) {
    return (
      <div className="milestone-primary-cell">
        <button
          type="button"
          className={`expand-row-btn effort-expand-btn ${row._isExpanded ? 'expanded' : ''}`.trim()}
          aria-label={row._isExpanded ? 'Collapse milestone row' : 'Expand milestone row'}
          aria-expanded={row._isExpanded}
          onClick={() => onToggle(row._groupKey)}
        >
          Expand
        </button>
        <span className="milestone-primary-value">{renderedValue}</span>
      </div>
    );
  }

  return (
    <div className="milestone-primary-cell">
      <span className="milestone-expand-placeholder" aria-hidden="true" />
      <span className="milestone-primary-value">{renderedValue}</span>
    </div>
  );
};

export const getOrderedColumns = (flags) => ([
  {
    key: 'po_number',
    label: 'PO Number',
    show: Boolean(flags.po_number_view),
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.po_number),
  },
  {
    key: 'po_link',
    label: 'PO Link',
    show: Boolean(flags.po_link_view),
    contentClassName: 'is-link',
    render: (row) => renderLinkValue(row.po_link),
  },
  {
    key: 'milestone_id',
    label: 'Milestone Id',
    show: true,
    sticky: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.milestone_id),
  },
  {
    key: 'milestone_status',
    label: 'Milestone Status',
    show: true,
    contentClassName: 'is-status',
    render: (row) => renderStatusValue(row.milestone_status),
  },
  {
    key: 'invoice_status',
    label: 'Invoice Status',
    show: true,
    contentClassName: 'is-status',
    render: (row) => renderStatusValue(row.invoice_status),
  },
  {
    key: 'milestone',
    label: 'Milestone',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.milestone),
  },
  {
    key: 'category',
    label: 'Category',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.category),
  },
  {
    key: 'cont_value_p',
    label: '% (Of Contract Value)',
    show: true,
    contentClassName: 'is-number',
    render: (row) => formatPercentValue(row.cont_value_p),
  },
  {
    key: 'amount',
    label: 'Amount (INR)',
    show: Boolean(flags.ms_amt),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.amount),
  },
  {
    key: 'cogs',
    label: 'COGS (INR)',
    show: Boolean(flags.ms_amt),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.cogs),
  },
  {
    key: 'ga',
    label: 'GA (INR)',
    show: Boolean(flags.ms_amt),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.ga),
  },
  {
    key: 'tp',
    label: 'TP (INR)',
    show: Boolean(flags.ms_amt),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.tp),
  },
  {
    key: 'due_date',
    label: 'Due Date',
    show: true,
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.due_date),
  },
  {
    key: 'replanned_due_date',
    label: 'Replanned Due Date',
    show: Boolean(flags.replanned_due_date_view),
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.replanned_due_date),
  },
  {
    key: 'invoice_id',
    label: 'Invoice Id',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.invoice_id),
  },
  {
    key: 'zoho_invoice_number',
    label: 'Zoho Invoice Number',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.zoho_invoice_number),
  },
  {
    key: 'invoice_id_related_to_credit_note',
    label: 'Invoice Id Related to Credit Note',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.invoice_id_related_to_credit_note),
  },
  {
    key: 'invoice_type',
    label: 'Invoice Type',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.invoice_type),
  },
  {
    key: 'payment_status',
    label: 'Payment Status',
    show: true,
    contentClassName: 'is-status',
    render: (row) => renderStatusValue(row.payment_status),
  },
  {
    key: 'invoice_amount',
    label: 'Invoice Amount (INR)',
    show: Boolean(flags.ms_inv),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.invoice_amount),
  },
  {
    key: 'invoice_cogs',
    label: 'Invoice COGS (INR)',
    show: Boolean(flags.ms_inv),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.invoice_cogs),
  },
  {
    key: 'invoice_ga',
    label: 'Invoice GA (INR)',
    show: Boolean(flags.ms_inv),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.invoice_ga),
  },
  {
    key: 'invoice_tp',
    label: 'Invoice TP (INR)',
    show: Boolean(flags.ms_inv),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.invoice_tp),
  },
  {
    key: 'invoice_amount_with_tax',
    label: 'Invoice Amount with Tax (INR)',
    show: Boolean(flags.ms_inv),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.invoice_amount_with_tax),
  },
  {
    key: 'invoice_date',
    label: 'Invoice Date',
    show: true,
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.invoice_date),
  },
  {
    key: 'revenue_recognition_date',
    label: 'Revenue Recognition Date',
    show: true,
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.revenue_recognition_date),
  },
  {
    key: 'received_date',
    label: 'Received Date',
    show: Boolean(flags.ms_rdt),
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.received_date),
  },
  {
    key: 'billed_effort_hrs',
    label: 'Billed Effort(hrs)',
    show: true,
    contentClassName: 'is-number',
    render: (row) => renderCellText(row.billed_effort_hrs),
  },
  {
    key: 'collection_credit_period',
    label: 'Collection Credit Period',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.collection_credit_period),
  },
  {
    key: 'date_variance',
    label: 'Variance',
    show: true,
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.date_variance),
  },
  {
    key: 'remarks',
    label: 'Remarks',
    show: Boolean(flags.ms_rmk),
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.remarks),
  },
  {
    key: 'collection_id',
    label: 'Collection Id',
    show: Boolean(flags.collection_amount_view),
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.collection_id),
  },
  {
    key: 'amount_to_collect',
    label: 'Amount to Collect (INR)',
    show: Boolean(flags.collection_amount_view),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.amount_to_collect),
  },
  {
    key: 'collection_due_date',
    label: 'Collection Due Date',
    show: Boolean(flags.collection_date_view),
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.collection_due_date),
  },
  {
    key: 'collection_projection_date',
    label: 'Collection Projection Date',
    show: Boolean(flags.collection_date_view),
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.collection_projection_date),
  },
  {
    key: 'collected_date',
    label: 'Collected Date',
    show: Boolean(flags.collection_date_view),
    contentClassName: 'is-date',
    render: (row) => formatDateValue(row.collected_date),
  },
  {
    key: 'collected_amount',
    label: 'Collected Amount (INR)',
    show: Boolean(flags.collection_amount_view),
    contentClassName: 'is-number',
    render: (row) => formatCurrencyValue(row.collected_amount),
  },
  {
    key: 'collection_comment',
    label: 'Collection Comment',
    show: Boolean(flags.collection_amount_view),
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.collection_comment),
  },
  {
    key: 'collection_remarks',
    label: 'Collection Remarks',
    show: Boolean(flags.collection_amount_view),
    contentClassName: 'is-text',
    render: (row) => renderCellText(row.collection_remarks),
  },
  {
    key: 'actions',
    label: 'Actions',
    show: Boolean(flags.ms_e),
    className: 'td-actions',
    contentClassName: 'is-actions',
    render: (row, callbacks) => renderActionButtons(row, flags, callbacks),
  },
]).filter((column) => column.show && !HIDDEN_COLLECTION_COLUMN_KEYS.has(column.key));

export const PROJECT_MILESTONE_COLUMNS = getOrderedColumns(PROJECT_MILESTONE_TABLE_FLAGS);
